import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { dashboardAPI } from '../api/api';
import axiosInstance from '../api/api';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import OnboardingTutorial from '../components/OnboardingTutorial';
import AdminViewBanner from '../components/AdminViewBanner';
import BookingPromoBanner from '../components/BookingPromoBanner';
import DashboardThemeToggle from '../components/DashboardThemeToggle';
import OnboardingChecklist from '../components/OnboardingChecklist';
import PullToRefresh from '../components/PullToRefresh';
import { StatsGridSkeleton } from '../components/Skeletons';
import RecentJobPhotos from '../components/RecentJobPhotos';
import CostOverviewWidget from '../components/CostOverviewWidget';
import { WeatherPanel } from '../components/WeatherPanel';
import { TelegramPromoCard } from '../components/TelegramPromoCard';
import { VoiceDictationPromoCard } from '../components/VoiceDictationPromoCard';
import { SubcontractingHub } from '../components/SubcontractingHub';
import { SubsPromoCard } from '../components/SubsPromoCard';
import { HomeownerActivitySummary } from '../components/contractor/HomeownerActivitySummary';
import MyPublicProfileBanner from '../components/contractor/MyPublicProfileBanner';
import VerificationStatusBanner from '../components/contractor/VerificationStatusBanner';
import { ContractorQuoteRequests } from '../components/contractor/ContractorQuoteRequests';
import { ContractorActiveProjects } from '../components/contractor/ContractorActiveProjects';
import ContractorChecklistsTab from '../components/contractor/ContractorChecklistsTab';
import ShoppingListsTab from '../components/contractor/ShoppingListsTab';
import { ContractorLeadsCTA } from '../components/contractor/ContractorLeadsCTA';
import { ContractorChatSidebar } from '../components/contractor/ContractorChatSidebar';
import { LiveLeadPreview } from '../components/contractor/LiveLeadPreview';
import { RecentActivityFeed } from '../components/contractor/RecentActivityFeed';
import { UsageBadge } from '../components/SubscriptionUI';
import { ScheduledCallsList } from '../components/ScheduleCall';
import NewInternalProjectModal from '../components/NewInternalProjectModal';
import { AISchedulerPromo } from '../components/AISchedulerPromo';
import { 
  Briefcase, Users, CurrencyDollar, Plus, FileText, ChatCircle, Gear,
  ShieldCheck, ChartBar, CalendarBlank, DownloadSimple, Question, ArrowRight,
  UsersThree, SpinnerGap, Camera, ChartPie, MapPin, Receipt,
  ClockCounterClockwise, FolderOpen, Lightning, ShoppingCart, Coins, TrendUp,
  House, Clock, CheckCircle, XCircle, PaperPlaneTilt, X, CaretDown, CaretUp,
  Hammer, Envelope, Phone, ArrowLeft, ChatCircleDots, BellRinging, Star, Copy,
  Scales, Gift, Sparkle, Translate, Microphone, Wallet, Crosshair
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContractorDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [userName, setUserName] = useState('');
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dashboardStats, setDashboardStats] = useState({ open_jobs: 0, total_applications: 0, month_spending: 0, hired_workers: 0 });
  const [recentPlans, setRecentPlans] = useState([]);
  const [leadsData, setLeadsData] = useState({ available: 0, purchased: 0, credits: 0 });
  const [quoteRequests, setQuoteRequests] = useState([]);
  const [quoteStats, setQuoteStats] = useState({ total: 0, pending: 0, quoted: 0 });
  const [activeProjects, setActiveProjects] = useState([]);
  const [walletBalance, setWalletBalance] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [conversations, setConversations] = useState([]);

  // Chat state
  const [chatOpen, setChatOpen] = useState(false);
  const [chatPartnerId, setChatPartnerId] = useState(null);
  const [chatPartnerName, setChatPartnerName] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const chatScrollRef = React.useRef(null);
  const chatPollRef = React.useRef(null);
  const [chatView, setChatView] = useState('list');

  // Data fetchers
  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await dashboardAPI.getContractorDashboard();
      if (response.data.success) { setDashboardStats(response.data.stats); setRecentPlans(response.data.recent_plans || []); }
    } catch (error) { console.error('Error fetching dashboard:', error); }
    finally { setLoading(false); }
  }, []);

  const fetchLeadsData = useCallback(async () => {
    try {
      const [marketplaceRes, purchasedRes, creditsRes] = await Promise.all([
        axiosInstance.get('/leads/marketplace?limit=1'), axiosInstance.get('/leads/marketplace/purchased'), axiosInstance.get('/credits/balance')
      ]);
      setLeadsData({ available: marketplaceRes.data?.total || 0, purchased: purchasedRes.data?.purchases?.length || 0, credits: creditsRes.data?.balance || 0 });
    } catch (error) { console.error('Error fetching leads data:', error); }
  }, []);

  const fetchQuoteRequests = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/contractor/quotes/requests');
      if (res.data?.success) { setQuoteRequests(res.data.requests || []); setQuoteStats(res.data.stats || { total: 0, pending: 0, quoted: 0 }); }
    } catch (error) { console.error('Error fetching quote requests:', error); }
  }, []);

  const fetchActiveProjects = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/project-tracker/active');
      if (res.data?.success) setActiveProjects(res.data.projects || []);
    } catch (error) { console.error('Error fetching active projects:', error); }
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/messages/unread/count`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const data = await res.json(); if (data.success) setUnreadCount(data.unread_count || 0); }
    } catch { /* ignore */ }
  }, []);

  const fetchConversations = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/messages/conversations`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const data = await res.json(); if (data.success) setConversations(data.conversations || []); }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/login?type=contractor'); return; }
    const storedType = localStorage.getItem('userType');
    if (storedType && storedType !== 'contractor' && storedType !== 'admin') {
      navigate(storedType === 'worker' ? '/worker-dashboard' : storedType === 'homeowner' ? '/homeowner-dashboard' : '/login');
      return;
    }
    const storedName = localStorage.getItem('userName');
    const storedUser = localStorage.getItem('user');
    if (storedName) { setUserName(storedName.split(' ')[0]); }
    else if (storedUser) { try { const u = JSON.parse(storedUser); if (u.name) setUserName(u.name.split(' ')[0]); } catch { /* ignore */ } }

    const onboardingCompleted = localStorage.getItem('constructhelp_onboarding_completed');
    const forceOnboarding = location.search.includes('onboarding=1');
    if (forceOnboarding || !onboardingCompleted) { setTimeout(() => setShowOnboarding(true), 500); }
    
    fetchDashboardData(); fetchLeadsData(); fetchQuoteRequests(); fetchActiveProjects(); fetchUnreadCount();
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) { const data = await res.json(); setWalletBalance(data.user?.wallet_balance || 0); }
      } catch { /* ignore */ }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect
  }, [navigate, location.key, fetchDashboardData, fetchLeadsData, fetchQuoteRequests, fetchActiveProjects, fetchUnreadCount]);

  const handleRestartTutorial = () => { localStorage.removeItem('constructhelp_onboarding_completed'); setShowOnboarding(true); };

  // Chat functions
  const openChatWith = (partnerId, partnerName) => {
    setChatPartnerId(partnerId); setChatPartnerName(partnerName); setChatView('chat'); setChatOpen(true); setChatMessages([]); setChatInput('');
  };
  const openInbox = () => { setChatView('list'); setChatPartnerId(null); setChatPartnerName(''); setChatOpen(true); fetchConversations(); };

  const fetchChatMessages = useCallback(async () => {
    if (!chatPartnerId) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/messages/${chatPartnerId}?limit=50`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const data = await res.json(); if (data.success) setChatMessages(data.messages || []); }
    } catch { /* ignore */ }
  }, [chatPartnerId]);

  React.useEffect(() => {
    if (chatOpen && chatPartnerId) { fetchChatMessages(); chatPollRef.current = setInterval(fetchChatMessages, 5000); return () => clearInterval(chatPollRef.current); }
    if (chatOpen && chatView === 'list') { fetchConversations(); const convPoll = setInterval(fetchConversations, 8000); return () => clearInterval(convPoll); }
    return () => clearInterval(chatPollRef.current);
  }, [chatOpen, chatPartnerId, fetchChatMessages, chatView, fetchConversations]);

  React.useEffect(() => { if (chatScrollRef.current) chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight; }, [chatMessages]);
  React.useEffect(() => { const interval = setInterval(fetchUnreadCount, 10000); return () => clearInterval(interval); }, [fetchUnreadCount]);

  const handleSendChat = async (e) => {
    e.preventDefault();
    const text = chatInput.trim();
    if (!text || chatSending) return;
    setChatSending(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/messages`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ receiver_id: chatPartnerId, content: text }) });
      setChatInput(''); await fetchChatMessages();
    } catch { /* ignore */ } finally { setChatSending(false); }
  };

  const currentUserId = localStorage.getItem('userId');

  return (
    <PullToRefresh onRefresh={() => Promise.all([fetchDashboardData(), fetchLeadsData(), fetchQuoteRequests(), fetchActiveProjects(), fetchUnreadCount()])}>
    <div className="ios-dash min-h-screen bg-[#F4F7FC] dark:bg-neutral-900 pb-24 transition-colors duration-300" data-testid="contractor-dashboard">
      <AdminViewBanner role="contractor" />
      {/* Header */}
      <div className="bg-[#2563EB] text-[#0F172A] py-6 border-b-4 border-[#0F172A] dark:border-[#64748B]">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-3xl md:text-4xl font-bold">Welcome, {userName}!</h1>
          <p className="text-[#94A3B8] text-lg mt-1">Manage your projects and crew</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <DashboardThemeToggle />
        <AISchedulerPromo role="contractor" />
        <BookingPromoBanner role="contractor" />
        <OnboardingChecklist role="contractor" />
        {/* Live Lead Preview — real-time toasts when a new matching lead is posted */}
        <LiveLeadPreview />

        {/* Today — needs your attention */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="today-strip">
          {[
            { label: 'New Leads', value: leadsData.available, icon: Lightning, path: '/leads-marketplace', color: '#2563EB', testId: 'today-leads' },
            { label: 'Quotes to Send', value: quoteStats.pending, icon: FileText, path: null, scrollTo: 'quote-requests', color: '#D97706', testId: 'today-quotes' },
            { label: 'Unread Messages', value: unreadCount, icon: ChatCircle, action: openInbox, color: '#7C3AED', testId: 'today-messages' },
            { label: 'Active Projects', value: activeProjects.length, icon: Briefcase, path: null, scrollTo: 'active-projects', color: '#059669', testId: 'today-projects' },
          ].map(({ label, value, icon: Icon, path, scrollTo, action, color, testId }) => (
            <button
              key={testId}
              onClick={() => {
                if (action) return action();
                if (scrollTo) return document.getElementById(scrollTo)?.scrollIntoView({ behavior: 'smooth' });
                if (path) navigate(path);
              }}
              className={`text-left bg-white dark:bg-neutral-800 border-2 rounded-xl p-3.5 shadow-[3px_3px_0px_#0F172A] dark:shadow-[3px_3px_0px_#2563EB] hover:-translate-y-0.5 active:translate-y-0 transition-all ${value > 0 ? 'border-[#0F172A] dark:border-neutral-600' : 'border-neutral-200 dark:border-neutral-700 opacity-70'}`}
              data-testid={testId}
            >
              <div className="flex items-center justify-between">
                <Icon size={20} weight="bold" style={{ color }} />
                <span className="text-2xl font-black text-neutral-900 dark:text-white">{value}</span>
              </div>
              <p className="text-[11px] font-bold uppercase tracking-wide text-neutral-500 dark:text-neutral-400 mt-1">{label}</p>
            </button>
          ))}
        </div>

        <WeatherPanel />
        <TelegramPromoCard />
        <SubsPromoCard />
        <VoiceDictationPromoCard />
        <SubcontractingHub />

        {/* Hero Tools — most-used contractor entry points */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="hero-tools">
          {/* AI Pricing Tool — NEW: Photo Vision */}
          <button
            onClick={() => navigate('/ai-pricing')}
            className="group relative overflow-hidden text-left bg-[#FEF08A] text-[#0F172A] rounded-xl border-2 border-[#0F172A] shadow-[4px_4px_0px_#0F172A] hover:shadow-[6px_6px_0px_#0F172A] hover:-translate-y-0.5 active:translate-y-0 active:shadow-[2px_2px_0px_#0F172A] transition-all p-5"
            data-testid="hero-ai-pricing-btn"
          >
            <span className="absolute top-2 right-2 bg-white text-[#475569] text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 border-2 border-[#0F172A] rounded">NEW</span>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-black/20 rounded-lg flex items-center justify-center border-2 border-white/30">
                <Camera size={26} weight="fill" className="text-[#0F172A]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-black uppercase tracking-widest opacity-80 flex items-center gap-1">
                  <Sparkle size={10} weight="fill" /> AI Tool
                </p>
                <h3 className="text-lg font-black leading-tight">AI Pricing Tool</h3>
              </div>
            </div>
            <p className="text-xs opacity-90 leading-relaxed">
              Snap photos → instant 3-tier estimates with materials breakdown.
            </p>
            <div className="mt-3 flex items-center gap-1 text-xs font-bold opacity-90 group-hover:opacity-100">
              Open Tool <ArrowRight size={14} weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </div>
          </button>

          {/* Leads Marketplace */}
          <button
            onClick={() => navigate('/leads-marketplace')}
            className="group relative overflow-hidden text-left bg-[#3B82F6] text-[#0F172A] rounded-xl border-2 border-[#0F172A] shadow-[4px_4px_0px_#0F172A] hover:shadow-[6px_6px_0px_#0F172A] hover:-translate-y-0.5 active:translate-y-0 active:shadow-[2px_2px_0px_#0F172A] transition-all p-5"
            data-testid="hero-leads-marketplace-btn"
          >
            {leadsData?.available_count > 0 && (
              <span className="absolute top-2 right-2 bg-white text-[#475569] text-[10px] font-black px-2 py-0.5 border-2 border-[#0F172A] rounded-full" data-testid="hero-leads-count">
                {leadsData.available_count} live
              </span>
            )}
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-black/20 rounded-lg flex items-center justify-center border-2 border-white/30">
                <ShoppingCart size={26} weight="bold" className="text-[#0F172A]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-black uppercase tracking-widest opacity-80">Marketplace</p>
                <h3 className="text-lg font-black leading-tight">Leads Marketplace</h3>
              </div>
            </div>
            <p className="text-xs opacity-90 leading-relaxed">
              Browse verified homeowner projects. Pay only for what you buy.
            </p>
            <div className="mt-3 flex items-center gap-1 text-xs font-bold opacity-90 group-hover:opacity-100">
              Browse Leads <ArrowRight size={14} weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </div>
          </button>

          {/* Find Workers */}
          <button
            onClick={() => navigate('/find-workers')}
            className="group relative overflow-hidden text-left bg-[#2563EB] text-[#0F172A] rounded-xl border-2 border-[#0F172A] shadow-[4px_4px_0px_#0F172A] hover:shadow-[6px_6px_0px_#0F172A] hover:-translate-y-0.5 active:translate-y-0 active:shadow-[2px_2px_0px_#0F172A] transition-all p-5"
            data-testid="hero-find-workers-btn"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-black/20 rounded-lg flex items-center justify-center border-2 border-white/30">
                <UsersThree size={26} weight="bold" className="text-[#0F172A]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-black uppercase tracking-widest opacity-80">Hiring</p>
                <h3 className="text-lg font-black leading-tight">Find Workers</h3>
              </div>
            </div>
            <p className="text-xs opacity-90 leading-relaxed">
              Search rated, vetted workers by trade, location, and availability.
            </p>
            <div className="mt-3 flex items-center gap-1 text-xs font-bold opacity-90 group-hover:opacity-100">
              Search Workers <ArrowRight size={14} weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </div>
          </button>

          {/* Voice Translator — EN ↔ ES */}
          <button
            onClick={() => navigate('/translator')}
            className="group relative overflow-hidden text-left bg-[#D1FAE5] text-[#0F172A] rounded-xl border-2 border-[#0F172A] shadow-[4px_4px_0px_#0F172A] hover:shadow-[6px_6px_0px_#0F172A] hover:-translate-y-0.5 active:translate-y-0 active:shadow-[2px_2px_0px_#0F172A] transition-all p-5"
            data-testid="hero-translator-btn"
          >
            <span className="absolute top-2 right-2 bg-white text-emerald-700 text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 border-2 border-[#0F172A] rounded">NEW</span>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-black/20 rounded-lg flex items-center justify-center border-2 border-white/30">
                <Microphone size={26} weight="fill" className="text-[#0F172A]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-black uppercase tracking-widest opacity-80 flex items-center gap-1">
                  <Translate size={10} weight="fill" /> 12 LANGUAGES
                </p>
                <h3 className="text-lg font-black leading-tight">Voice Translator</h3>
              </div>
            </div>
            <p className="text-xs opacity-90 leading-relaxed">
              Speak any of 12 languages — instant voice translation for your crew.
            </p>
            <div className="mt-3 flex items-center gap-1 text-xs font-bold opacity-90 group-hover:opacity-100">
              Open Translator <ArrowRight size={14} weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </div>
          </button>
        </div>

        {/* Winning work: leads CTA right after hero tools */}
        <ContractorLeadsCTA leadsData={leadsData} />

        {/* My Public Profile Banner */}
        <MyPublicProfileBanner userType="contractor" />

        {/* Verified Pro status / CTA */}
        <VerificationStatusBanner />

        {/* Primary Actions */}
        <div className="flex flex-wrap gap-3 mb-6">
          <Button onClick={() => navigate('/post-job')} className="h-12 px-5 bg-[#2563EB] hover:bg-[#1D4ED8] text-[#0F172A] font-bold rounded-lg border-2 border-[#0F172A] shadow-[3px_3px_0px_#0F172A] hover:shadow-[4px_4px_0px_#0F172A] active:translate-y-1 active:shadow-none transition-all" data-testid="post-job-btn"><Plus size={20} weight="bold" className="mr-2" /> Post New Job</Button>
          <Button onClick={() => setShowNewProjectModal(true)} className="h-12 px-5 bg-[#3B82F6] hover:bg-[#1E40AF] text-[#0F172A] font-bold rounded-lg border-2 border-[#0F172A] shadow-[3px_3px_0px_#0F172A] hover:shadow-[4px_4px_0px_#0F172A] active:translate-y-1 active:shadow-none transition-all" data-testid="new-internal-project-btn"><Briefcase size={20} weight="bold" className="mr-2" /> New Project</Button>
          <Button
            onClick={() => {
              document.getElementById('daily-checklists')?.scrollIntoView({ behavior: 'smooth' });
              window.dispatchEvent(new Event('open-checklist-create'));
            }}
            className="h-12 px-5 bg-[#0F172A] hover:bg-neutral-800 text-white font-bold rounded-lg border-2 border-[#3B82F6] shadow-[3px_3px_0px_#3B82F6] hover:shadow-[4px_4px_0px_#3B82F6] active:translate-y-1 active:shadow-none transition-all"
            data-testid="voice-checklist-action-btn"
          >
            <Microphone size={20} weight="fill" className="mr-2 text-[#475569]" /> Voice Checklist
            <span className="ml-2 text-[9px] bg-[#3B82F6] text-[#0F172A] px-1 py-0.5 font-black uppercase tracking-widest">New</span>
          </Button>
          <button onClick={() => navigate('/my-jobs')} className="flex items-center gap-2 h-12 px-4 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-white font-bold rounded-lg border-2 border-[#0F172A] dark:border-neutral-700 shadow-[3px_3px_0px_#0F172A] dark:shadow-[3px_3px_0px_#2563EB] hover:shadow-[4px_4px_0px_#0F172A] active:translate-y-1 active:shadow-none transition-all" data-testid="my-jobs-btn"><Briefcase size={20} weight="bold" /> My Jobs</button>
          <button onClick={openInbox} className="flex items-center gap-2 h-12 px-4 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-white font-bold rounded-lg border-2 border-[#0F172A] dark:border-neutral-700 shadow-[3px_3px_0px_#0F172A] dark:shadow-[3px_3px_0px_#2563EB] hover:shadow-[4px_4px_0px_#0F172A] active:translate-y-1 active:shadow-none transition-all relative" data-testid="messages-btn"><ChatCircle size={20} weight="bold" /> Messages {unreadCount > 0 && <span className="absolute -top-1 -right-1 min-w-[20px] h-5 bg-[#3B82F6] text-[#0F172A] text-xs font-bold flex items-center justify-center px-1 border border-[#0F172A]">{unreadCount > 99 ? '99+' : unreadCount}</span>}</button>
        </div>

        {/* ACTIVE WORK — respond to quotes, run projects, today's checklists */}
        <div id="quote-requests" className="scroll-mt-24">
          <ContractorQuoteRequests quoteRequests={quoteRequests} quoteStats={quoteStats} openChatWith={openChatWith} onRefresh={fetchQuoteRequests} />
        </div>
        <div id="active-projects" className="scroll-mt-24">
          <ContractorActiveProjects activeProjects={activeProjects} openChatWith={openChatWith} />
        </div>
        <ScheduledCallsList />
        <div id="daily-checklists" className="my-6 scroll-mt-24"><ContractorChecklistsTab /></div>
        <div id="shopping-lists" className="my-6 scroll-mt-24"><ShoppingListsTab /></div>

        {/* Tool Groups */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Jobs & Workers */}
          <div className="bg-white dark:bg-neutral-800 border-2 border-[#0F172A] dark:border-neutral-700 rounded-xl p-4 shadow-[3px_3px_0px_#0F172A] dark:shadow-[3px_3px_0px_#2563EB]">
            <h3 className="text-xs font-black uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3">Jobs & Workers</h3>
            <div className="flex flex-wrap gap-2">
              {[
                { path: '/job-templates', icon: Copy, label: 'Templates', testId: 'job-templates-btn', cls: 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700' },
                { path: '/favorite-workers', icon: Star, label: 'Favorites', testId: 'favorite-workers-btn', cls: 'bg-orange-50 dark:bg-orange-900/30 text-[#475569] border-orange-300 dark:border-orange-700', fill: true },
                { path: '/my-jobs', icon: Scales, label: 'Compare Bids', testId: 'compare-bids-nav-btn', cls: 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 border-violet-300 dark:border-violet-700' },
                { path: '/browse-worker-teams', icon: Users, label: 'Hire Teams', testId: 'browse-teams-btn', cls: 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700' },
                { path: '/hunter-leads', icon: Crosshair, label: 'Hunter Leads', testId: 'hunter-leads-btn', cls: 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700' },
              ].map(({ path, icon: Icon, label, testId, cls, fill }) => (
                <button key={testId} onClick={() => navigate(path)} className={`flex items-center gap-1.5 h-10 px-3 text-sm font-bold rounded-lg border-2 hover:opacity-80 transition-all ${cls}`} data-testid={testId}>
                  <Icon size={16} weight={fill ? 'fill' : 'bold'} /> {label}
                </button>
              ))}
            </div>
          </div>

          {/* Payments & Finance */}
          <div className="bg-white dark:bg-neutral-800 border-2 border-[#0F172A] dark:border-neutral-700 rounded-xl p-4 shadow-[3px_3px_0px_#0F172A] dark:shadow-[3px_3px_0px_#2563EB]">
            <h3 className="text-xs font-black uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3">Payments & Finance</h3>
            <div className="flex flex-wrap gap-2">
              {[
                { path: '/escrow', icon: ShieldCheck, label: 'Escrow', testId: 'escrow-btn', cls: 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700' },
                { path: '/wallet', icon: Wallet, label: 'Wallet & Bank', testId: 'wallet-btn', cls: 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700' },
                { path: '/invoices', icon: Receipt, label: 'Invoices', testId: 'invoices-btn', cls: 'bg-lime-100 dark:bg-lime-900/50 text-lime-700 dark:text-lime-300 border-lime-300 dark:border-lime-700' },
                { path: '/reports', icon: ChartPie, label: 'Reports', testId: 'reports-btn', cls: 'bg-pink-100 dark:bg-pink-900/50 text-pink-700 dark:text-pink-300 border-pink-300 dark:border-pink-700' },
                { path: '/referrals', icon: Gift, label: 'Refer & Earn', testId: 'refer-earn-btn', cls: 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700' },
              ].map(({ path, icon: Icon, label, testId, cls }) => (
                <button key={testId} onClick={() => navigate(path)} className={`flex items-center gap-1.5 h-10 px-3 text-sm font-bold rounded-lg border-2 hover:opacity-80 transition-all ${cls}`} data-testid={testId}>
                  <Icon size={16} weight="bold" /> {label}
                </button>
              ))}
            </div>
          </div>

          {/* Planning & Tools */}
          <div className="bg-white dark:bg-neutral-800 border-2 border-[#0F172A] dark:border-neutral-700 rounded-xl p-4 shadow-[3px_3px_0px_#0F172A] dark:shadow-[3px_3px_0px_#2563EB]">
            <h3 className="text-xs font-black uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3">Planning & Tools</h3>
            <div className="flex flex-wrap gap-2">
              {[
                { path: '/project-planning', icon: ChartBar, label: 'Projects', testId: 'projects-btn', cls: 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700' },
                { path: '/ai-scheduler', icon: CalendarBlank, label: 'AI Scheduler', testId: 'ai-scheduler-btn', cls: 'bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-700' },
                { path: '/calendar', icon: CalendarBlank, label: 'Calendar', testId: 'calendar-btn', cls: 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700' },
                { path: '/team', icon: UsersThree, label: 'Team', testId: 'team-btn', cls: 'bg-teal-100 dark:bg-teal-900/50 text-teal-700 dark:text-teal-300 border-teal-300 dark:border-teal-700' },
                { path: '/timesheets', icon: ClockCounterClockwise, label: 'Timesheets', testId: 'timesheets-btn', cls: 'bg-cyan-100 dark:bg-cyan-900/50 text-cyan-700 dark:text-cyan-300 border-cyan-300 dark:border-cyan-700' },
                { path: '/my-jobs', icon: FolderOpen, label: 'Documents', testId: 'documents-btn', cls: 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 border-indigo-300 dark:border-indigo-700' },
                { path: '/contractor-control-panel', icon: Gear, label: 'Settings', testId: 'control-panel-btn', cls: 'bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 border-neutral-300 dark:border-neutral-700' },
              ].map(({ path, icon: Icon, label, testId, cls }) => (
                <button key={testId} onClick={() => navigate(path)} className={`flex items-center gap-1.5 h-10 px-3 text-sm font-bold rounded-lg border-2 hover:opacity-80 transition-all ${cls}`} data-testid={testId}>
                  <Icon size={16} weight="bold" /> {label}
                </button>
              ))}
              <button onClick={handleRestartTutorial} className="flex items-center gap-1.5 h-10 px-3 text-sm bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 font-bold rounded-lg border-2 border-neutral-300 dark:border-neutral-700 hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-all" data-testid="help-tutorial-btn"><Question size={16} weight="bold" /> Help</button>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        {loading ? (
          <StatsGridSkeleton />
        ) : (
          <>
            {(quoteRequests.length > 0 || activeProjects.length > 0 || unreadCount > 0) && (
              <HomeownerActivitySummary quoteStats={quoteStats} activeProjects={activeProjects} unreadCount={unreadCount} walletBalance={walletBalance} />
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white dark:bg-neutral-800 border-2 border-[#0F172A] dark:border-neutral-700 rounded-xl p-5 shadow-[4px_4px_0px_#0F172A] dark:shadow-[4px_4px_0px_#2563EB]"><Briefcase size={32} weight="bold" className="text-[#64748B] mb-3" /><p className="text-sm font-bold text-neutral-500 dark:text-neutral-400">Open Jobs</p><p className="text-3xl font-bold text-neutral-900 dark:text-white">{dashboardStats.open_jobs}</p></div>
              <div className="bg-white dark:bg-neutral-800 border-2 border-[#0F172A] dark:border-neutral-700 rounded-xl p-5 shadow-[4px_4px_0px_#0F172A] dark:shadow-[4px_4px_0px_#2563EB]"><FileText size={32} weight="bold" className="text-purple-600 mb-3" /><p className="text-sm font-bold text-neutral-500 dark:text-neutral-400">Applications</p><p className="text-3xl font-bold text-neutral-900 dark:text-white">{dashboardStats.total_applications}</p></div>
              <div className="bg-white dark:bg-neutral-800 border-2 border-[#0F172A] dark:border-neutral-700 rounded-xl p-5 shadow-[4px_4px_0px_#0F172A] dark:shadow-[4px_4px_0px_#2563EB]"><CurrencyDollar size={32} weight="bold" className="text-emerald-600 mb-3" /><p className="text-sm font-bold text-neutral-500 dark:text-neutral-400">Spent This Month</p><p className="text-3xl font-bold text-emerald-600">${dashboardStats.month_spending?.toLocaleString() || 0}</p></div>
              <div className="bg-white dark:bg-neutral-800 border-2 border-[#0F172A] dark:border-neutral-700 rounded-xl p-5 shadow-[4px_4px_0px_#0F172A] dark:shadow-[4px_4px_0px_#2563EB]"><Users size={32} weight="bold" className="text-[#64748B] mb-3" /><p className="text-sm font-bold text-neutral-500 dark:text-neutral-400">Hired Workers</p><p className="text-3xl font-bold text-neutral-900 dark:text-white">{dashboardStats.hired_workers}</p></div>
            </div>
          </>
        )}

        {/* Extracted Components */}

        <div className="mb-6"><RecentActivityFeed /></div>

        {/* Recent Project Plans */}
        <div className="bg-white dark:bg-neutral-800 border-2 border-[#0F172A] dark:border-neutral-700 rounded-xl p-6 shadow-[4px_4px_0px_#0F172A] dark:shadow-[4px_4px_0px_#2563EB]">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-neutral-900 dark:text-white">Recent Projects</h2>
            <button onClick={() => navigate('/project-planning')} className="flex items-center gap-2 text-[#64748B] font-bold hover:underline">View All <ArrowRight size={20} weight="bold" /></button>
          </div>
          {recentPlans.length === 0 ? (
            <div className="text-center py-8">
              <Briefcase size={48} weight="light" className="text-neutral-300 dark:text-neutral-600 mx-auto mb-4" />
              <p className="text-neutral-500 dark:text-neutral-400 mb-4">No projects yet</p>
              <button onClick={() => navigate('/project-planning')} className="h-12 px-6 bg-[#2563EB] text-[#0F172A] font-bold rounded-xl border-2 border-[#0F172A] shadow-[3px_3px_0px_#0F172A] hover:shadow-[4px_4px_0px_#0F172A] active:translate-y-1 active:shadow-none transition-all">Create Your First Project</button>
            </div>
          ) : (
            <div className="space-y-4">
              {recentPlans.map((plan) => (
                <div key={plan.id} onClick={() => navigate(`/project-planning/${plan.id}`)} className="border-2 border-neutral-200 dark:border-neutral-700 rounded-xl p-4 hover:border-[#2563EB] transition-all cursor-pointer">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="font-bold text-neutral-900 dark:text-white text-lg">{plan.title}</h3>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold mt-2 border-2 ${plan.status === 'completed' ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-emerald-400' : 'bg-[#2563EB]/10 text-[#64748B] border-[#64748B]'}`}>{plan.status === 'completed' ? 'Completed' : 'Active'}</span>
                    </div>
                    <div className="text-right"><p className="text-2xl font-bold text-neutral-900 dark:text-white">{plan.progress}%</p><p className="text-sm text-neutral-500 dark:text-neutral-400">{plan.task_count} tasks</p></div>
                  </div>
                  <Progress value={plan.progress} className="h-3" />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6"><RecentJobPhotos userType="contractor" /></div>
        <div className="mt-6"><CostOverviewWidget /></div>
      </div>

      <OnboardingTutorial isOpen={showOnboarding} onClose={() => setShowOnboarding(false)} onComplete={() => setShowOnboarding(false)} />

      <ContractorChatSidebar
        chatOpen={chatOpen} setChatOpen={setChatOpen} chatView={chatView} setChatView={setChatView}
        chatPartnerId={chatPartnerId} setChatPartnerId={setChatPartnerId} chatPartnerName={chatPartnerName}
        conversations={conversations} chatMessages={chatMessages} chatInput={chatInput} setChatInput={setChatInput}
        chatSending={chatSending} chatScrollRef={chatScrollRef} currentUserId={currentUserId}
        openChatWith={openChatWith} handleSendChat={handleSendChat} fetchConversations={fetchConversations}
      />
      <NewInternalProjectModal
        open={showNewProjectModal}
        onClose={() => setShowNewProjectModal(false)}
        onCreated={(p) => {
          setShowNewProjectModal(false);
          if (p && p._id) navigate(`/project/${p._id}`);
        }}
      />
    </div>
    </PullToRefresh>
  );
};

export default ContractorDashboard;
